import { ref } from 'vue';
import type {
  NotificationData,
  OpenNotificationParams,
} from './models/notification';

const NEVER_AUTO_CLOSE = -1;

class NotificationService {
  readonly notifications = ref<NotificationData[]>([]);

  #idCounter = 0;
  readonly #timers = new Map<string, ReturnType<typeof setTimeout>>();

  open(params: OpenNotificationParams): string {
    const { title, text, type, timeout = 5000, component } = params;
    const key = this.#dedupKey(params);

    const existing = this.notifications.value.find((n) => n.key === key);
    if (existing) {
      existing.count += 1;
      this.#scheduleAutoClose(existing.id, existing.timeout);
      return existing.id;
    }

    const id = `notification-${++this.#idCounter}`;
    this.notifications.value.push({
      id,
      key,
      count: 1,
      title,
      text,
      type,
      timeout,
      component,
    });

    this.#scheduleAutoClose(id, timeout);

    return id;
  }

  close(id: string): void {
    this.#clearTimer(id);

    const index = this.notifications.value.findIndex((n) => n.id === id);
    if (index >= 0) {
      this.notifications.value.splice(index, 1);
    }
  }

  pause(id: string): void {
    this.#clearTimer(id);
  }

  resume(id: string): void {
    const notification = this.notifications.value.find((n) => n.id === id);
    if (notification) {
      this.#scheduleAutoClose(id, notification.timeout);
    }
  }

  #scheduleAutoClose(id: string, timeout: number): void {
    this.#clearTimer(id);
    if (timeout === NEVER_AUTO_CLOSE) {
      return;
    }
    this.#timers.set(
      id,
      setTimeout(() => this.close(id), timeout),
    );
  }

  #clearTimer(id: string): void {
    const timer = this.#timers.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      this.#timers.delete(id);
    }
  }

  #dedupKey({ title, text, type, component }: OpenNotificationParams): string {
    const componentKey = component ? JSON.stringify(component.props ?? {}) : '';
    return [type ?? '', title ?? '', text, componentKey].join('|');
  }
}

export const notificationService = new NotificationService();
